// src/features/portfolio-optimization/components/AssetTearsheet.tsx
//
// Zone 3 — the ML "Glass Box", part 2: the per-asset Tearsheet.
//
// A right-hand slide-out panel that opens when a row in the Conviction table
// is clicked. It puts the ML's full reasoning for ONE name on a single
// surface: the headline view (side, calibrated probability, view return Q,
// uncertainty Omega), the descriptive metadata (sector, company, target
// weight, volatility) and a Radar / Spider chart that profiles the asset
// across its normalised factors so the relative shape (vs the rest of the
// book) is legible at a glance.
//
// The radar axes are normalised to 0..100 IN THE PAGE (which holds the whole
// cross-section); this component stays purely presentational.

import { Link } from 'react-router-dom';
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
} from 'recharts';

import type { MlInsightSignal, PortfolioAsset } from '../api';

export interface RadarAxis {
    /** Short axis label, e.g. 'Conviction', 'Edge', 'Z-Score'. */
    axis: string;
    /** Normalised value 0..100. */
    value: number;
}

interface AssetTearsheetProps {
    open: boolean;
    onClose: () => void;
    signal: MlInsightSignal | null;
    holding?: PortfolioAsset;
    radar: RadarAxis[];
    /** Selected ticker — drives the panel + deep-link even when no ML view
     *  exists for it (e.g. a Tactical Daily Signals row outside the active
     *  Black-Litterman view set). */
    symbol?: string | null;
}

const Stat = ({
    label,
    value,
    accent,
}: {
    label: string;
    value: string;
    accent?: string;
}) => (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
            {label}
        </p>
        <p className={`mt-0.5 font-mono font-bold ${accent ?? 'text-slate-900 dark:text-white'}`}>
            {value}
        </p>
    </div>
);

export const AssetTearsheet = ({
    open,
    onClose,
    signal,
    holding,
    radar,
    symbol,
}: AssetTearsheetProps) => {
    const isLong = (signal?.ml_side ?? 1) >= 0;
    const displaySymbol = signal?.symbol ?? holding?.symbol ?? symbol ?? null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ${
                    open ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                aria-hidden="true"
            />

            {/* Panel */}
            <aside
                className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700 transition-transform duration-300 ease-out flex flex-col ${
                    open ? 'translate-x-0' : 'translate-x-full'
                }`}
                role="dialog"
                aria-label="Asset tearsheet"
            >
                {displaySymbol && (
                    <>
                        <header className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-slate-700">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                                        {displaySymbol}
                                    </h2>
                                    {signal && (
                                        <span
                                            className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${
                                                isLong
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
                                                    : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800'
                                            }`}
                                        >
                                            {isLong ? 'LONG' : 'SHORT'}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                                    {holding?.company_name ?? displaySymbol}
                                    {holding?.sector ? ` · ${holding.sector}` : ''}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="shrink-0 p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                                aria-label="Close tearsheet"
                            >
                                <svg
                                    className="w-5 h-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                >
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </header>

                        <div className="flex-1 overflow-y-auto p-5 space-y-6">
                            {/* Headline ML diagnostics + descriptive metadata. */}
                            {signal || holding ? (
                                <div className="grid grid-cols-2 gap-3">
                                    {signal && (
                                        <>
                                            <Stat
                                                label="Conviction (p)"
                                                value={signal.master_probability.toFixed(2)}
                                                accent={
                                                    signal.master_probability >= 0.5
                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                        : 'text-rose-600 dark:text-rose-400'
                                                }
                                            />
                                            <Stat
                                                label="Expected Return"
                                                value={`${(signal.expected_return_q * 100).toFixed(2)}%`}
                                            />
                                            <Stat
                                                label="Uncertainty Ω"
                                                value={signal.uncertainty_omega.toFixed(4)}
                                            />
                                            <Stat
                                                label="Phase-1 Z"
                                                value={
                                                    signal.phase_1_z_score !== null
                                                        ? `${signal.phase_1_z_score > 0 ? '+' : ''}${signal.phase_1_z_score.toFixed(2)}σ`
                                                        : '—'
                                                }
                                            />
                                        </>
                                    )}
                                    {holding && (
                                        <>
                                            <Stat
                                                label="Target Weight"
                                                value={`${(holding.target_weight * 100).toFixed(2)}%`}
                                                accent="text-emerald-600 dark:text-emerald-400"
                                            />
                                            <Stat
                                                label="Volatility"
                                                value={
                                                    holding.annualized_volatility !== null
                                                        ? `${(holding.annualized_volatility * 100).toFixed(1)}%`
                                                        : '—'
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    No detailed ML view is available for this symbol on
                                    this date. Open the full stock page for fundamentals,
                                    price history, analyst grades and earnings.
                                </p>
                            )}

                            {/* Radar / spider profile (only when an ML view exists). */}
                            {signal && radar.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                                        Factor Profile
                                    </h3>
                                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-2">
                                        Normalised 0–100 vs the active book.
                                    </p>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RadarChart data={radar} outerRadius="72%">
                                                <PolarGrid stroke="rgba(148,163,184,0.3)" />
                                                <PolarAngleAxis
                                                    dataKey="axis"
                                                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                                                />
                                                <PolarRadiusAxis
                                                    domain={[0, 100]}
                                                    tick={false}
                                                    axisLine={false}
                                                />
                                                <Radar
                                                    dataKey="value"
                                                    stroke={isLong ? '#10b981' : '#f43f5e'}
                                                    fill={isLong ? '#10b981' : '#f43f5e'}
                                                    fillOpacity={0.4}
                                                />
                                            </RadarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer CTA — deep-link into the full stock-info page. */}
                        <div className="p-5 border-t border-slate-200 dark:border-slate-700">
                            <Link
                                to={`/dashboard/stock-info-page?symbol=${encodeURIComponent(displaySymbol)}`}
                                onClick={onClose}
                                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition-colors shadow-sm"
                            >
                                More Info
                                <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M5 12h14M13 6l6 6-6 6" />
                                </svg>
                            </Link>
                        </div>
                    </>
                )}
            </aside>
        </>
    );
};

export default AssetTearsheet;
